import { Link } from '~/types'
import {
  EmployeeFieldsFragment,
  EmployeePositionFieldsFragment,
  EmployeesPositionsDocument,
  LanguageId,
  MediaImageFragment,
} from '../graphql/generated'
import type { Composer } from 'vue-i18n'

export interface Employee {
  id: number
  name: string
  email: string
  phone: string
  position: string
  description: string
  link: Link
  image: MediaImageFragment
}

const createTransformResponse =
  (t: Composer['t']) =>
  (data: EmployeePositionFieldsFragment[]): Employee[] => {
    if (!data.length) return []

    return data
      .filter((employeePosition) => employeePosition.reverseFieldPositionEmployee?.entities?.length !== 0)
      .map((employeePosition) => {
        const position = employeePosition.fieldWorkTitle ?? ''
        const employee = employeePosition.reverseFieldPositionEmployee.entities?.[0] as EmployeeFieldsFragment

        if (!employee) return null

        return {
          id: employee.id!,
          position,
          name: `${employee.fieldAcademicTitle ?? ''} ${employee.fieldFirstName} ${employee.fieldLastName}`,
          image: employee.fieldImage?.entity as MediaImageFragment,
          phone: employee.fieldPhone ?? '',
          email: employee.fieldEmail ?? '',
          description: employee.fieldDescription ?? '',
          link: {
            url: employee.entityUrl?.path || '',
            text: t('employee.list.teaser.cta'),
          },
        }
      })
      .filter(Boolean)
  }

const getItems = async (
  query: any,
  variables: any,
  transform: any,
): Promise<{
  items: Employee[]
  total: number
}> => {
  const { data } = await query({
    query: EmployeesPositionsDocument,
    variables,
  })

  return {
    items: transform(data.employeePositionQuery?.entities as EmployeePositionFieldsFragment[]),
    total: data.employeePositionQuery?.count || 0,
  }
}

export const useEmployees = async ({
  filter,
}: {
  filter: {
    institution: string
    sector: string[]
  }
}) => {
  const route = useRoute()
  const { clients } = useApollo()
  const { t } = useI18n()
  const { languageCODE } = useLocale()

  const transform = createTransformResponse(t)

  const perPage = 18
  const page = ref(route.query.page ? +route.query.page : 1)

  const setQueryParams = async () => {
    const params = {
      ...(filter.institution ? { institution: filter.institution } : {}),
      ...(filter.sector ? { sector: filter.sector } : {}),
      ...(page.value ? { page: page.value.toString() } : {}),
      scroll: 'false',
    }

    await navigateTo({ query: params, replace: true })
  }

  const institution_sector = computed(() => {
    if (filter.sector.length) return filter.sector
    return [filter.institution]
  })

  const getVariables = () => ({
    institution_sector: institution_sector.value,
    offset: perPage * (page.value - 1),
    perPage,
    now: new Date(),
    language: languageCODE.value as LanguageId,
  })

  const { state: data, ready } = useAsyncState({
    key: 'employees',
    fetcher: () => getItems(clients!.default.query, getVariables(), transform),
    fallback: { items: [], total: 0 },
  })

  const items = computed(() => data.value.items)
  const pages = computed(() => Math.ceil(data.value.total / perPage))

  watch([() => ({ ...filter }), page], async ([currentFilter], [previousFilter]) => {
    if (
      (currentFilter.institution !== previousFilter.institution || currentFilter.sector !== previousFilter.sector) &&
      page.value !== 1
    )
      return (page.value = 1) // triggers a new watch callback
    await setQueryParams()
    // Use direct call for reactive updates
    data.value = await getItems(clients!.default.query, getVariables(), transform)
  })

  await ready()

  return {
    items,
    page,
    pages,
  } as const
}
